/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * CRITICAL: This page checks if user already has an org and redirects away.
 * TenantContext skips its query/redirect on /onboarding to prevent loops.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { addToast, Button } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '~/hooks/useAuth';
import { ApiError } from '~/services/api/client';
import {
  createInvitation,
  createOrganization,
  getMyOrganizations,
} from '~/services/api/organizations';
import { clearActiveOrgId } from '~/utils/activeOrg';

import { Header } from '~/components/Header';
import { LoadingSpinner } from '~/components/LoadingSpinner';

import { OnboardingDetails } from './OnboardingDetails';
import { OrganizationDescription } from './OrganizationDescription';
import { OrganizationInvite } from './OrganizationInvite';
import { OrganizationSteps } from './OrganizationSteps';
import {
  defaultOnboardingFormValues,
  type OnboardingFormData,
  onboardingFormSchema,
} from './schemas';

const STEPS = [
  { label: 'Details', optional: false },
  { label: 'Description', optional: true },
  { label: 'Invite', optional: true },
];

export function Onboarding(): JSX.Element {
  const { loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);

  // Check if user already has an organization - if so, redirect away
  // This is separate from TenantContext's query which is disabled on /onboarding
  const { data: existingOrgs, isLoading: isCheckingOrgs } = useQuery({
    queryKey: ['organization', 'onboarding-check'],
    queryFn: getMyOrganizations,
    enabled: !!user,
    retry: false, // 403 "no_organization" is expected for new users
  });

  // Clear activeOrgId on onboarding page mount (new user or creating new org)
  useEffect(() => {
    clearActiveOrgId();
  }, []);

  // Redirect if user already has an organization
  useEffect(() => {
    if (existingOrgs && existingOrgs.length > 0) {
      void navigate('/', { replace: true });
    }
  }, [existingOrgs, navigate]);

  const form = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingFormSchema),
    mode: 'onBlur',
    defaultValues: defaultOnboardingFormValues,
  });

  const { isPending, mutate } = useMutation({
    mutationKey: ['createOrganization'],
    mutationFn: async (data: OnboardingFormData) => {
      const org = await createOrganization({
        name: data.name,
        slug: data.slug,
        description: data.description || undefined,
      });

      // 2. Send invitations (fire-and-forget, don't block on failures)
      const failedInvites: Array<{ email: string; reason: string }> = [];
      for (const invite of data.invites) {
        try {
          await createInvitation(org.id, invite.email, invite.role);
        } catch (err) {
          const reason = err instanceof Error ? err.message : 'Unknown error';
          failedInvites.push({ email: invite.email, reason });
        }
      }

      return { org, failedInvites };
    },
    onSuccess: ({ failedInvites }) => {
      // Show success toast
      addToast({
        description: 'Organization created successfully!',
        color: 'success',
        icon: <BuildingOfficeIcon />,
      });

      // Show warning if some invites failed
      if (failedInvites.length > 0) {
        addToast({
          description: `Some invitations failed: ${failedInvites.map(f => f.email).join(', ')}`,
          color: 'warning',
        });
      }

      // Reset (not just invalidate) organization queries so TenantContext starts fresh.
      // invalidateQueries only marks the cached 403 "no_organization" error as stale —
      // the error stays in cache and TenantContext will redirect back to /onboarding
      // before the refetch completes, causing an infinite loop.
      void queryClient.resetQueries({ queryKey: ['organization'] });

      // Redirect to dashboard
      void navigate('/', { replace: true });
    },
    onError: (error: Error) => {
      // Handle 409 Conflict (slug already taken)
      if (error instanceof ApiError && error.status === 409) {
        addToast({
          description:
            error.message || 'This slug is already taken. Please choose a different one.',
          color: 'danger',
          icon: <ExclamationTriangleIcon />,
        });
        setStep(0); // Return to step 1 to fix slug
        return;
      }

      // Generic error
      addToast({
        description: error.message || 'Failed to create organization. Please try again.',
        color: 'danger',
      });
    },
  });

  const handleNext = useCallback(async () => {
    // Validate current step fields before proceeding
    let isValid = false;

    if (step === 0) {
      isValid = await form.trigger(['name', 'slug']);
    } else if (step === 1) {
      isValid = true; // Description is optional, always valid
    } else if (step === 2) {
      isValid = await form.trigger(['invites']);
    }

    if (isValid && step < STEPS.length - 1) {
      setStep(prev => prev + 1);
    }
  }, [step, form]);

  const handleBack = useCallback(() => {
    if (step > 0) {
      setStep(prev => prev - 1);
    }
  }, [step]);

  const handleSkip = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep(prev => prev + 1);
    }
  }, [step]);

  const handleSubmit = useCallback(
    (data: OnboardingFormData) => {
      mutate(data);
    },
    [mutate],
  );

  // Loading state - includes auth AND org check
  if (authLoading || isCheckingOrgs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="mb-4">Please sign in to continue.</p>
          <Link
            to="/login"
            className="text-primary underline decoration-primary/50 hover:decoration-primary"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  const isLastStep = step === STEPS.length - 1;
  const isOptionalStep = STEPS[step].optional;

  return (
    <>
      <Header layout="logout" />
      <div className="container mx-auto p-8">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Create Your Organization</h1>
            <p>Set up your workspace to get started with Compounds.</p>
          </div>

          <OrganizationSteps steps={STEPS} currentStep={step} />

          {/* Form content */}
          <div className="bg-content1 rounded-xl p-6 shadow-sm border border-divider">
            <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
              {/* Step content */}
              {step === 0 && <OnboardingDetails form={form} />}
              {step === 1 && <OrganizationDescription form={form} />}
              {step === 2 && <OrganizationInvite form={form} />}

              {isLastStep && (
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-divider">
                  <div>
                    <Button type="button" onPress={handleBack} isDisabled={isPending}>
                      Back
                    </Button>
                  </div>
                  <Button color="primary" type="submit" isDisabled={isPending}>
                    {isPending ? <LoadingSpinner /> : 'Create Organization'}
                  </Button>
                </div>
              )}
            </form>

            {!isLastStep && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-divider">
                <div>
                  {step > 0 && (
                    <Button onPress={handleBack} isDisabled={isPending}>
                      Back
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  {isOptionalStep && (
                    <Button variant="light" onPress={handleSkip} isDisabled={isPending}>
                      Skip
                    </Button>
                  )}
                  <Button color="primary" onPress={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-center mt-6">
            Already invited to an organization?{' '}
            <Link
              to="/invitations"
              className="text-muted underline decoration-muted/50 hover:decoration-muted"
            >
              View pending invitations
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
